/**
 * Target tongue shapes for the 3D model, on the measured anatomy (anatomy.gen.ts): the midline
 * dorsum of each pose, tip to root, in mm (origin at the upper incisors' edge, [z forward, y up]),
 * which the rig (tongueRig.ts) maps the anatomical tongue onto, plus the side raise or drop.
 *
 * Sources, and how each number was chosen (documented = stated in a source; estimated = read off
 * published images and set within that picture's proportions; all are checked by tests):
 * - Bunched /r/: Zhou et al. 2008 (JASA 123:4466, MRI: tongue tip lowered, dorsum raised, a long
 *   palatal constriction further back than retroflex, the root drawn toward the pharyngeal wall,
 *   no clear sublingual space); Delattre and Freeman 1968 (pharyngeal constriction); the
 *   bunched-/r/ teaching references the user supplied (2026-10-11): the body bunched up under the
 *   junction of hard and soft palate, the tip pulled back from the lower teeth pointing slightly
 *   down, the root back toward the throat, lips slightly rounded. Gap at the peak 3-5 mm and root
 *   to wall 6-8 mm: estimated.
 * - Retroflex /r/: Zhou et al. 2008 (tip raised, dorsum lowered, shorter palatal constriction
 *   further forward, a large mid-cavity); tip 5-7 mm below the post-alveolar palate: estimated.
 * - /w/: a back-raised, lip-rounded glide like /u/ (dorsum toward the soft palate); gap 4-6 mm:
 *   estimated.
 * - /l/ (dark, as in syllable-final position and in children's lateralised errors): Narayanan,
 *   Alwan and Haker 1997 (MRI and EPG: linguo-alveolar contact, inward lateral compression giving
 *   flow channels along the sides, convex posterior cross-sections); dorsum lowered and root
 *   retracted for dark /l/.
 * - Children: 69 % of typically developing children use only bunched /r/ and none only retroflex
 *   (Benway et al. 2024, JSLHR 67:4246): bunched is the main teaching shape.
 */
import { smoothCurve, type PoseId, type ProfilePoint } from './articulation';
import { HARD_PALATE, PHARYNGEAL_WALL, TONGUE_DORSUM_REST, UVULA } from './anatomy.gen';
import { curveFrames } from './tongueRig';

/**
 * The soft palate's oral surface when raised for speech (all these sounds are oral): from the
 * hard palate's end back, curving down to the uvula, which hangs below the velopharyngeal
 * closure and ends short of the throat wall (its oral surface is drawn here; the closure is on
 * its upper side). Estimated from the anatomy: the uvula of the BodyParts3D body.
 */
export const SOFT_PALATE_RAISED: readonly ProfilePoint[] = [
  HARD_PALATE[HARD_PALATE.length - 1] ?? [-54, 23],
  [-61, 22],
  [-67, 19.5],
  [-72, 15.5],
  [UVULA[0] + 5, UVULA[1] - 9], // the uvula hangs below the closure, short of the throat wall
];

/**
 * The floor of the mouth under the tongue body along the midline, where the extrinsic muscles
 * (genioglossus, hyoglossus) attach it; the rig settles the tongue's underside onto it so the tongue
 * does not float. Estimated from the BodyParts3D body: under the tongue body, above the hyoid.
 */
export const FLOOR_OF_MOUTH: readonly ProfilePoint[] = [
  [-10, -10],
  [-20, -12.5],
  [-35, -14.5],
  [-50, -15.5],
  [-62, -17.5],
  [-72, -23.5],
];

/**
 * The roof of the mouth along the midline: hard palate from behind the alveolar ridge (the bone in
 * front of it is behind the incisors and lip, not the roof), then the raised soft palate.
 */
export const ROOF: readonly ProfilePoint[] = [
  ...HARD_PALATE.filter(([z]) => z <= -6),
  ...SOFT_PALATE_RAISED.slice(1),
];

/**
 * The airway's back wall: the lining (mucosa and the tissue over the constrictor muscles) lies
 * in front of the muscles' surface that PHARYNGEAL_WALL measures. About 3 mm: estimated.
 */
export const MUCOSA_MM = 3;
export const AIRWAY_WALL: readonly ProfilePoint[] = PHARYNGEAL_WALL.map(
  ([z, y]) => [z + MUCOSA_MM, y] as const,
);

export interface TongueTarget {
  dorsum: readonly ProfilePoint[];
  /** How much thicker the front gets than at rest (tongueRig.ts); 1 when absent. */
  frontThickness?: number;
  /** Side raise (+, bracing and a midline groove) or drop (-, lateral channels), mm, tip to root. */
  sides: readonly number[];
  /**
   * Lateral bracing, tip to root (0 none, 1 the side margins against the upper molars and gums;
   * tongueRig.ts). /r/ only (docs/design/articulation-spec.md: sides braced, central groove).
   */
  bracing?: readonly number[];
}

export const TONGUE_TARGETS: Readonly<Record<PoseId, TongueTarget>> = {
  // At rest the tip lies behind the lower front teeth (articulation-spec.md): the measured
  // tongue (BodyParts3D, a cadaver's) has it higher, so the front is lowered smoothly, up to 6 mm at
  // the tip, fading out by 30 mm back; the back keeps its measured shape.
  rest: {
    dorsum: TONGUE_DORSUM_REST.map(([z, y]) => {
      const k = Math.min(1, Math.max(0, (z + 30) / 16));
      return [z, y - 6 * k * k * (3 - 2 * k)] as const;
    }),
    sides: [],
  },
  rBunched: {
    dorsum: [
      [-15, -5], // tip low and rounded, just behind the lower front teeth
      [-18.5, -1],
      [-23, 3.5],
      [-28.5, 7.5], // the front slopes smoothly down from the peak (air under the alveolar ridge)
      [-34.5, 11.5],
      [-40.5, 15],
      [-46, 17.5],
      [-51, 18.5], // the bunched peak under the junction of hard and soft palate, a clear gap
      [-57.5, 17.5],
      [-64, 15.5],
      [-70, 7],
      [-75, -1],
      [-77, -7], // root drawn back toward the pharyngeal wall (narrow throat)
      [-77.5, -14],
      [-77, -20],
      [-75, -25], // the root continuing down toward the epiglottis
    ],
    frontThickness: 1.3,
    sides: [0, 1, 1.5, 1.5, 1.5, 1, 0],
    bracing: [0, 0.3, 1, 1, 1, 0.6, 0],
  },
  rRetroflex: {
    dorsum: [
      [-17, 8], // tip raised and turned slightly back, below the post-alveolar palate
      [-15, 4.5],
      [-17, 0.5],
      [-23, 0],
      [-31, 3],
      [-39, 5.5],
      [-47, 6.5], // dorsum lowered, below the tip (a large mid-cavity)
      [-56, 6],
      [-64, 3],
      [-71, -3],
      [-75, -9], // root drawn back
      [-74, -16],
      [-71, -21],
    ],
    sides: [0, 2, 3, 3, 2, 1, 0],
    bracing: [0, 0.3, 1, 1, 1, 0.6, 0],
  },
  w: {
    dorsum: [
      [-12, -2], // tip low behind the lower teeth
      [-17, 2],
      [-24, 6],
      [-31, 9.5],
      [-38, 13],
      [-45, 15.5],
      [-52, 16.5],
      [-58, 15.5], // back of the tongue up toward the soft palate
      [-65, 11],
      [-71, 3],
      [-73, -6],
      [-72, -14],
      [-70, -21],
    ],
    sides: [0, 0, 1, 1, 1, 0, 0],
  },
  l: {
    dorsum: [
      [-8, 11.7], // tip on the alveolar ridge
      [-12, 8],
      [-18, 4],
      [-27, 5],
      [-36, 7],
      [-45, 8], // dorsum lowered
      [-55, 6],
      [-63, -1],
      [-70, -6],
      [-75, -12], // root drawn back (dark /l/)
      [-73, -18],
      [-70, -21],
    ],
    sides: [0, -3, -5, -5, -4, -2, 0],
  },
};

/** The roof's height at depth z (mm), by linear interpolation; undefined outside it. */
export function roofHeight(z: number): number | undefined {
  for (let i = 0; i + 1 < ROOF.length; i++) {
    const [z0, y0] = ROOF[i] ?? [0, 0];
    const [z1, y1] = ROOF[i + 1] ?? [0, 0];
    if (z <= z0 && z >= z1 && z0 !== z1) return y0 + ((y1 - y0) * (z - z0)) / (z1 - z0);
  }
  return undefined;
}

/** The airway's back wall's depth at height y (mm). */
export function wallDepth(y: number): number {
  let best = AIRWAY_WALL[0] ?? [-81, 0];
  for (const p of AIRWAY_WALL) if (Math.abs(p[1] - y) < Math.abs(best[1] - y)) best = p;
  return best[0];
}

/** Points per blended dorsum. */
const BLEND_POINTS = 48;

/**
 * A target between rest (t = 0) and a pose (t = 1): both dorsums resampled by arc length and
 * blended point by point; the side raise blended too.
 */
export function blendTarget(a: TongueTarget, b: TongueTarget, t: number): TongueTarget {
  // Smooth curves through the target points (Catmull-Rom), so the tongue has no creases.
  const fa = curveFrames(smoothCurve(a.dorsum, BLEND_POINTS), BLEND_POINTS);
  const fb = curveFrames(smoothCurve(b.dorsum, BLEND_POINTS), BLEND_POINTS);
  const dorsum = fa.map((p, i) => {
    const q = fb[i] ?? p;
    return [p.z + (q.z - p.z) * t, p.y + (q.y - p.y) * t] as const;
  });
  const n = Math.max(a.sides.length, b.sides.length);
  const at = (s: readonly number[], i: number) =>
    s.length ? (s[Math.round((i / Math.max(1, n - 1)) * (s.length - 1))] ?? 0) : 0;
  const sides = Array.from({ length: n }, (_, i) => at(a.sides, i) * (1 - t) + at(b.sides, i) * t);
  const thick = (a.frontThickness ?? 1) * (1 - t) + (b.frontThickness ?? 1) * t;
  const nb = Math.max(a.bracing?.length ?? 0, b.bracing?.length ?? 0);
  const braceAt = (s: readonly number[] | undefined, i: number) =>
    s?.length ? (s[Math.round((i / Math.max(1, nb - 1)) * (s.length - 1))] ?? 0) : 0;
  const brace = Array.from(
    { length: nb },
    (_, i) => braceAt(a.bracing, i) * (1 - t) + braceAt(b.bracing, i) * t,
  );
  return { dorsum, sides, frontThickness: thick, ...(nb ? { bracing: brace } : {}) };
}
